import { useCallback, useEffect, useRef, useState } from "react";

type UseMidiPlayerResult = {
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  loading: boolean;
  error: string | null;
};

export function useMidiPlayer(midiUrl: string | null): UseMidiPlayerResult {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const playerRef = useRef<any>(null);
  const sequenceRef = useRef<any>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedAtRef = useRef<number>(0);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      playerRef.current?.stop();
    } catch (_) {}
    setIsPlaying(false);
    setCurrentTime(0);
    pausedAtRef.current = 0;
  }, []);

  const pause = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      playerRef.current?.pause();
    } catch (_) {}
    setIsPlaying(false);
    pausedAtRef.current = startTimeRef.current + (Date.now() - startTimeRef.current) / 1000;
  }, []);

  const play = useCallback(async () => {
    if (!midiUrl) return;
    setError(null);

    try {
      if (!playerRef.current) {
        setLoading(true);
        const mm = await import("@magenta/music");
        const { urlToNoteSequence, Player } = mm;

        const seq = await urlToNoteSequence(midiUrl);
        sequenceRef.current = seq;
        const totalDuration =
          seq.totalTime ??
          (seq.notes?.length
            ? Math.max(...seq.notes.map((n: any) => (n.startTime || 0) + (n.duration || 0)))
            : 0);
        setDuration(totalDuration);

        const player = new Player();
        playerRef.current = player;
        await player.resumeContext();
        await player.start(seq);
        setIsPlaying(true);
        startTimeRef.current = Date.now();
        pausedAtRef.current = 0;

        intervalRef.current = setInterval(() => {
          const elapsed = (Date.now() - startTimeRef.current) / 1000;
          setCurrentTime(Math.min(elapsed, totalDuration));
          if (elapsed >= totalDuration) {
            stop();
          }
        }, 100);
        setLoading(false);
      } else {
        const player = playerRef.current;
        const seq = sequenceRef.current;
        if (seq) {
          await player.resumeContext();
          await player.start(seq, undefined, pausedAtRef.current);
          startTimeRef.current = Date.now() - pausedAtRef.current * 1000;
          setCurrentTime(pausedAtRef.current);
          setIsPlaying(true);
          const totalDuration = seq.totalTime ?? duration;
          intervalRef.current = setInterval(() => {
            const elapsed = (Date.now() - startTimeRef.current) / 1000;
            setCurrentTime(Math.min(elapsed, totalDuration));
            if (elapsed >= totalDuration) {
              stop();
            }
          }, 100);
        }
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to play MIDI");
      setLoading(false);
    }
  }, [midiUrl, duration, stop]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      try {
        playerRef.current?.stop();
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    if (!midiUrl) {
      playerRef.current = null;
      sequenceRef.current = null;
      stop();
    }
  }, [midiUrl, stop]);

  return { play, pause, stop, isPlaying, currentTime, duration, loading, error };
}
